import React, { ReactNode, ComponentType, type ReactElement } from 'react'
import styled from 'styled-components'

import transition from 'styles/transition'
import { getToken as token } from 'theming'

import type { ThemeToken } from 'theming'

type ThemeColors = keyof {
  [ColorToken in ThemeToken as ColorToken extends `color-${infer Token}`
    ? Token
    : never]: ColorToken
}

const Wrapper = styled.span<{ $color?: ThemeColors }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &,
  & > svg {
    ${transition({
      property: 'transform',
    })}

    /*
     * prop('$color', 'currentColor')
     * Not using the prop helper function since we can't return
     * an evaluated token from it
     */
    color: ${(props) =>
      props.$color ? token(`color-${props.$color}` as ThemeToken, props) : 'currentColor'};

    fill: currentColor;
  }
`

export interface IconProps<T> {
  name: keyof T
  size?: string | number
  width?: string | number
  height?: string | number
  className?: string
  color?: ThemeColors
}

export type IconMapping = Record<string, ComponentType<Omit<IconProps<string>, 'name'>>>

export function UnknownIcon(): ReactElement {
  return <span aria-label="Unknown icon">&#9785;</span>
}

function IconFactory(map: IconMapping): ComponentType<IconProps<typeof map>> {
  function getIcon(name: keyof typeof map): ComponentType<Omit<IconProps<typeof map>, 'name'>> {
    name = String(name || '').toLowerCase()

    if (!(name in map)) return UnknownIcon

    return map[name]
  }

  return function Icon(props: IconProps<typeof map>) {
    const { name, size, width = '1em', height = '1em', className, color, ...rest } = props

    const I = getIcon(name)
    return (
      <Wrapper className={className} $color={color}>
        <I width={size || width} height={size || height} {...rest} />
      </Wrapper>
    )
  }
}

export default IconFactory
